import {
	Component,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { JobsDataServiceProvider } from '@services/job/jobs.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { Location } from '@angular/common';
import { Job } from '@models/models';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';

@Component({
	selector: 'job-task-block',
	template: require('./job-task-block.html'),
	styles: [require('./job-task-block.scss')]
})
export class JobTaskBlockComponent implements OnInit, OnDestroy {
	@Output() editJobModalTrigger: EventEmitter<Job> = new EventEmitter<Job>();
	unsubscribe: Subject<void> = new Subject();
	jobs: Job[] = [];
	pageRequest: any;
	itemsQuantity: number;
	pagesQuantity: number;

	readonly PAGE_REQUEST_DEFAULT = {
		filterBy: 'active_with_next_task_due',
		paging: {
			pageNumber: 0,
			pageSize: 10
		},
		sortCriterias: [
			{
				fieldName: 'nexTaskDueDate',
				sortAscending: true
			}
		]
	};
	private allowedFilters: string[];

	constructor(
		private jobsDataService: JobsDataServiceProvider,
		private messageService: MessageService,
		private timeService: TimeServiceProvider,
		private brandsDataService: BrandsDataServiceProvider,
		private location: Location
	) {}

	ngOnInit(): void {
		this.configureCommunicationAdapter();
		this.subscribeToBrandsChange();
		this.pageRequest = this.PAGE_REQUEST_DEFAULT;
		this.getJobsData();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	isFilterAllowed(filter: string): boolean {
		if (!this.allowedFilters) {
			return false;
		}

		return this.allowedFilters.includes(filter);
	}

	orderByField(filter: string): void {
		if (this.pageRequest.sortCriterias[0].fieldName === filter) {
			this.pageRequest.sortCriterias[0].sortAscending = !this.pageRequest
				.sortCriterias[0].sortAscending;
		} else {
			this.pageRequest.sortCriterias[0].fieldName = filter;
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}
		this.pageRequest.paging.pageNumber = 0;

		this.getJobsData();
	}

	showJob(jobId): void {
		this.location.go(`jobs/view/${jobId}`);
	}

	showEditJobModal(index: number): void {
		this.editJobModalTrigger.emit(this.jobs[index]);
	}

	paginationToDefaults(): void {
		this.pageRequest = this.PAGE_REQUEST_DEFAULT;
		delete this.pageRequest.paging.searchToken;
		this.getJobsData();
	}

	updateModel(resetPagination: boolean): void {
		if (resetPagination) {
			this.paginationToDefaults();
		} else {
			this.getJobsData();
		}
	}

	updateShootsModel(resetPagination: boolean): void {
		window.snCommunicationAdapter.callMethod(
			'updateShootsModel',
			resetPagination
		);
	}

	updatePaymentsModel(resetPagination: boolean): void {
		window.snCommunicationAdapter.callMethod(
			'updateUpcomingPaymentsModel',
			resetPagination
		);
	}

	updateJobsData(pageRequest): void {
		this.pageRequest = pageRequest;
		this.getJobsData();
	}

	private getJobsData(): void {
		this.jobsDataService
			.getJobOnDashboard(this.pageRequest)
			.subscribe(
				(data): void => this.successGetDataJobs(data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private successGetDataJobs(data): void {
		this.pageRequest.paging.searchToken = data.searchToken;
		this.jobs = this.handleJobTask(JSON.parse(JSON.stringify(data.items)));
		this.itemsQuantity = data ? data.totalSize : 0;
		this.pagesQuantity = Math.ceil(
			this.itemsQuantity / this.pageRequest.paging.pageSize
		);
		this.allowedFilters = data.sortingFields;
	}

	private handleJobTask(jobs) {
		jobs.forEach(job => {
			job.workflow.nextTask.late = false;
			const dayMulti = this.addDayMulti(job.workflow.nextTask.overdueDays);

			if (job.workflow.nextTask.overdueDays === 0) {
				job.workflow.nextTask.nextTaskDueDays = 'today';
			} else if (job.workflow.nextTask.overdueDays > 0) {
				job.workflow.nextTask.nextTaskDueDays = `${job.workflow.nextTask
					.overdueDays + dayMulti} ago`;
				job.workflow.nextTask.late = true;
			} else {
				job.workflow.nextTask.nextTaskDueDays = `in ${-job.workflow.nextTask
					.overdueDays}${dayMulti}`;
			}
		});

		return jobs;
	}

	private addDayMulti(day: number): string {
		let dayMulti;
		if (day === 1 || day === -1) {
			dayMulti = ' day';
		} else {
			dayMulti = ' days';
		}
		return dayMulti;
	}

	private calcDate(date1, date2): number {
		const diff = Math.floor(date1.getTime() - date2.getTime());
		const day = 1000 * 60 * 60 * 24;
		const days = Math.floor(diff / day);
		return days;
	}

	private configureCommunicationAdapter(): void {
		window.snCommunicationAdapter.registerMethod(
			'updateJobTasksModel',
			this.updateModel.bind(this)
		);
	}

	private subscribeToBrandsChange(): void {
		this.brandsDataService.brandsChange
			.pipe(
				takeUntil(this.unsubscribe),
				filter((data): boolean => !!data)
			)
			.subscribe((): void => this.getJobsData());
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=ndy1wyp4lwc!./web/modern/components/dashboard/job-task-block/job-task-block.ts